import { z } from 'zod'

/** Roles del panel. El rol se resuelve por email, no se elige en la UI. */
export const roleSchema = z.enum(['admin', 'ops', 'comercial'])
export type Role = z.infer<typeof roleSchema>

/** Etiquetas de rol para la UI. */
export const ROLE_LABELS: Record<Role, string> = {
  admin: 'Admin',
  ops: 'Ops / Delivery',
  comercial: 'Comercial',
}

/** Estado semántico de una métrica. `unknown` = sin benchmark definido. */
export const healthSchema = z.enum(['ok', 'warning', 'critical', 'unknown'])
export type Health = z.infer<typeof healthSchema>

/**
 * De dónde salió un dato. `manual` es carga humana; el resto son adapters.
 *
 * `langfuse` es el quinto sistema de origen, el que trajo el módulo `/usage`:
 * tokens, modelo, costo y trazas por período salen de ahí. Está acá y no en un
 * enum aparte porque el badge de proveniencia es uno solo para todo el panel.
 */
export const sourceKindSchema = z.enum([
  'manual',
  'stripe',
  'plane',
  'plataforma',
  'search_console',
  'langfuse',
])
export type SourceKind = z.infer<typeof sourceKindSchema>

/** Nombre legible de cada fuente, para el badge de proveniencia. */
export const SOURCE_LABELS: Record<SourceKind, string> = {
  manual: 'manual',
  stripe: 'stripe',
  plane: 'plane',
  plataforma: 'plataforma',
  search_console: 'search console',
  langfuse: 'langfuse',
}

/** Qué tan viejo es el dato. `expired` genera alerta en Overview. */
export const freshnessSchema = z.enum(['fresh', 'stale', 'expired'])
export type Freshness = z.infer<typeof freshnessSchema>

export const provenanceSchema = z.object({
  source: sourceKindSchema,
  /** ISO 8601. null = nunca se cargó. */
  updatedAt: z.string().nullable(),
  /** Nombre de la persona que lo cargó. null si vino de un adapter. */
  updatedBy: z.string().nullable(),
  freshness: freshnessSchema,
})
export type Provenance = z.infer<typeof provenanceSchema>

export const unitSchema = z.enum([
  'usd',
  'percent',
  'count',
  'months',
  'ratio',
  'position',
])
export type Unit = z.infer<typeof unitSchema>

export const userSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  nombre: z.string(),
  rol: roleSchema,
  activo: z.boolean(),
})
export type User = z.infer<typeof userSchema>

export const userRefSchema = z.object({ id: z.string(), nombre: z.string() })
export type UserRef = z.infer<typeof userRefSchema>

/** 'YYYY-MM'. Es la clave de período en todo el contrato. */
export const periodoSchema = z
  .string()
  .regex(/^\d{4}-\d{2}$/, 'formato esperado YYYY-MM')

/** Prioridad genérica, se usa en clientes, canales y queries de SEO. */
export const prioridadSchema = z.enum(['alta', 'media', 'baja'])
export type Prioridad = z.infer<typeof prioridadSchema>
